import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/modules/shared/ui/components/select"
import { Trash2, Plus, AlertTriangle } from "lucide-react"
import { toast } from "sonner"
import { Alert, AlertDescription } from "@/modules/shared/ui/components/alert"
import { ScorecardTemplate, ScorecardCriterionInput as CriterionInputEntity } from "@/modules/recruitment/entities/ScorecardTemplate"
import { useScorecardTemplate, useSaveScorecardTemplate } from "@/modules/recruitment/hooks/useScorecards"

interface DraftCriterion {
  key: string
  id?: string
  label: string
  description: string
  weight: number
}

function toDraft(template: ScorecardTemplate | null | undefined): DraftCriterion[] {
  if (!template || template.criteria.length === 0) {
    return [{ key: crypto.randomUUID(), label: "", description: "", weight: 3 }]
  }
  return template.criteria
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((c) => ({
      key: c.id,
      id: c.id,
      label: c.label,
      description: c.description ?? "",
      weight: c.weight,
    }))
}

interface ScorecardTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  vacancyId: string
}

export function ScorecardTemplateDialog({
  open,
  onOpenChange,
  organizationId,
  vacancyId,
}: ScorecardTemplateDialogProps) {
  const { data: template } = useScorecardTemplate(organizationId, vacancyId)
  const saveTemplate = useSaveScorecardTemplate(organizationId, vacancyId)

  const [criteria, setCriteria] = useState<DraftCriterion[]>(() => toDraft(template))

  useEffect(() => {
    if (open) {
      setCriteria(toDraft(template))
    }
  }, [open, template])

  const updateCriterion = (key: string, patch: Partial<DraftCriterion>) => {
    setCriteria((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)))
  }

  const addCriterion = () => {
    setCriteria((prev) => [...prev, { key: crypto.randomUUID(), label: "", description: "", weight: 3 }])
  }

  const removeCriterion = (key: string) => {
    setCriteria((prev) => prev.filter((c) => c.key !== key))
  }

  const handleSave = () => {
    const trimmed = criteria.filter((c) => c.label.trim().length > 0)
    if (trimmed.length === 0) {
      toast.error("Add at least one criterion.")
      return
    }

    const payload: CriterionInputEntity[] = trimmed.map((c, index) => ({
      id: c.id,
      label: c.label.trim(),
      description: c.description.trim() || null,
      weight: c.weight,
      orderIndex: index,
    }))

    saveTemplate.mutate(payload, {
      onSuccess: () => onOpenChange(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Interview scorecard template</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {!!template?.submittedScorecardCount && (
            <Alert className="border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/10">
              <AlertTriangle className="size-4 text-amber-600 dark:text-amber-500" />
              <AlertDescription>
                {template.submittedScorecardCount} submitted scorecard
                {template.submittedScorecardCount === 1 ? "" : "s"} already exist for this
                vacancy. Saving changes here will mark {template.submittedScorecardCount === 1 ? "it" : "them"} as
                outdated and ask the panellist{template.submittedScorecardCount === 1 ? "" : "s"} to review and
                resubmit.
              </AlertDescription>
            </Alert>
          )}

          {criteria.map((criterion) => (
            <div key={criterion.key} className="flex gap-2 items-start border border-border rounded-md p-3">
              <div className="flex-1 space-y-2">
                <Input
                  value={criterion.label}
                  onChange={(e) => updateCriterion(criterion.key, { label: e.target.value })}
                  placeholder="Criterion, e.g. System design"
                />
                <Input
                  value={criterion.description}
                  onChange={(e) => updateCriterion(criterion.key, { description: e.target.value })}
                  placeholder="Guidance for the panelist (optional)"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Weight</Label>
                <Select
                  value={String(criterion.weight)}
                  onValueChange={(value) => updateCriterion(criterion.key, { weight: Number(value) })}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((w) => (
                      <SelectItem key={w} value={String(w)}>{w}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCriterion(criterion.key)}
                aria-label="Remove criterion"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}

          <Button type="button" variant="outline" onClick={addCriterion}>
            <Plus className="size-4" />
            Add criterion
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saveTemplate.isPending}>
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
